import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { ARTICLES, CATEGORIES, readingTime } from "@/data/articles";
import { assetUrl } from "@/lib/api";
import { useDocumentMeta } from "@/lib/seo";

const FEATURED_SLUG = "definir-budget-travaux";
const FILTERS = ["Tous", ...CATEGORIES];

const INSPIRATIONS = [
  { title: "Cuisine & pièce de vie", img: "/api/static/models/bastide-int2.webp", alt: "Cuisine ouverte chaleureuse en bois clair et teintes crème" },
  { title: "Salle de bains", img: "/api/static/models/patio-int4.webp", alt: "Salle de bains claire aux matériaux naturels" },
  { title: "Couleurs & matières", img: "/api/static/models/source-int2.webp", alt: "Ambiance intérieure mêlant couleurs douces et matières naturelles" },
  { title: "Optimiser les espaces", img: "/api/static/models/canopee-int4.webp", alt: "Petit bureau aménagé avec astuce dans un espace compact" },
];

const ArticleCard = ({ article, testid }) => (
  <Link to={`/conseils/${article.slug}`} data-testid={testid} className="group block">
    <div className="overflow-hidden rounded-3xl">
      <img
        src={article.img}
        alt={article.imgAlt}
        loading="lazy"
        className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
      />
    </div>
    <div className="mt-5 px-1">
      <p className="text-[11px] font-bold uppercase tracking-wider text-cognac">{article.category}</p>
      <h3 className="mt-2 font-display text-2xl font-medium leading-snug text-navy transition-colors duration-300 group-hover:text-sage">
        {article.title}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-stone-500">{article.excerpt}</p>
      <p className="mt-3 text-xs font-semibold text-stone-400">{readingTime(article)} min de lecture</p>
      <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-navy transition-colors duration-300 group-hover:text-cognac">
        Lire le conseil
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </span>
    </div>
  </Link>
);

export default function ConseilsPage() {
  const [filter, setFilter] = useState("Tous");

  useDocumentMeta(
    "Conseils & inspirations — Rénovation, aménagement, chantier | CozyHome",
    "Budget, matériaux, organisation, professionnels, chantier… CozyHome vous aide à mieux comprendre les différentes étapes d'un projet pour avancer avec plus de clarté."
  );

  const featured = ARTICLES.find((a) => a.slug === FEATURED_SLUG);
  const showFeatured = filter === "Tous";
  const list = filter === "Tous"
    ? ARTICLES.filter((a) => a.slug !== FEATURED_SLUG)
    : ARTICLES.filter((a) => a.category === filter);

  return (
    <PageShell>
      <PageHero
        testid="page-conseils-hero"
        label="Conseils & inspirations"
        title={<>Les bonnes décisions commencent par <span className="italic text-sage">les bonnes informations.</span></>}
        sub="Budget, matériaux, organisation, professionnels, chantier… CozyHome vous aide à mieux comprendre les différentes étapes d'un projet pour avancer avec plus de clarté."
      />

      <section className="bg-ivory pb-20 sm:pb-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          {/* Navigation éditoriale */}
          <nav data-testid="conseils-filters" aria-label="Catégories" className="flex flex-wrap gap-2.5">
            {FILTERS.map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`conseils-filter-${c === "Tous" ? "tous" : c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setFilter(c)}
                aria-pressed={filter === c}
                className={`rounded-full border-2 px-5 py-2.5 text-sm font-bold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cognac ${
                  filter === c
                    ? "border-navy bg-navy text-white"
                    : "border-[#EDEAE3] bg-white text-navy hover:border-cognac/50 hover:text-cognac"
                }`}
              >
                {c}
              </button>
            ))}
          </nav>

          {/* À la une */}
          {showFeatured && (
            <Reveal>
              <article
                data-testid="conseil-featured"
                className="mt-12 grid grid-cols-1 items-center gap-8 rounded-3xl bg-white p-5 sm:p-7 lg:grid-cols-2 lg:gap-12"
              >
                <div className="overflow-hidden rounded-2xl">
                  <img
                    src={featured.img}
                    alt={featured.imgAlt}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    className="aspect-[16/10] w-full object-cover"
                  />
                </div>
                <div className="px-2 pb-4 lg:pb-0">
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-cognac">À la une</p>
                  <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">
                    {featured.title}
                  </h2>
                  <p className="mt-4 max-w-lg text-sm leading-relaxed text-stone-500 sm:text-base">
                    {featured.excerpt}
                  </p>
                  <Link
                    to={`/conseils/${featured.slug}`}
                    data-testid="conseil-featured-cta"
                    className="group mt-7 inline-flex items-center gap-2.5 rounded-full bg-navy px-7 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-sage"
                  >
                    Lire le conseil
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </article>
            </Reveal>
          )}

          {/* Articles */}
          <div data-testid="conseils-grid" className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((a, i) => (
              <Reveal key={a.slug} delay={(i % 3) * 0.08}>
                <ArticleCard article={a} testid={`conseil-card-${a.slug}`} />
              </Reveal>
            ))}
          </div>

          {/* Inspirations */}
          <Reveal>
            <div data-testid="conseils-inspirations" className="mt-24">
              <div className="max-w-2xl">
                <h2 className="font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">
                  Trouver <span className="italic text-cognac">l'inspiration.</span>
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-stone-500 sm:text-base">
                  Une rénovation commence parfois par une image, une matière ou une façon
                  différente d'imaginer un espace.
                </p>
              </div>
              <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {INSPIRATIONS.map((card, i) => (
                  <Reveal key={card.title} delay={i * 0.08}>
                    <figure data-testid={`inspiration-card-${i}`} className="group">
                      <div className="relative overflow-hidden rounded-3xl">
                        <img
                          src={assetUrl(card.img)}
                          alt={card.alt}
                          loading="lazy"
                          className="aspect-[3/4] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                        />
                        <span className="absolute left-4 top-4 rounded-full bg-ivory/90 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-navy backdrop-blur-sm">
                          Inspiration
                        </span>
                      </div>
                      <figcaption className="mt-4 px-1 font-display text-lg font-medium text-navy">
                        {card.title}
                      </figcaption>
                    </figure>
                  </Reveal>
                ))}
              </div>
              <p className="mt-8 text-xs leading-relaxed text-stone-400">
                Visuels présentés à titre d'inspiration : ils ne correspondent pas
                nécessairement à des réalisations CozyHome.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
